/**
 * GWP (Gift With Purchase) Manager - Dual Campaign Support
 *
 * Supports two independent campaigns that can run simultaneously:
 *   1. Threshold — cart total >= $X triggers a free gift
 *   2. BOGO — buying a trigger product triggers a different free gift
 *
 * Each campaign tracks its own gift via `_giftCampaign` line-item property
 * ("threshold" or "bogo"), so gifts never collide.
 *
 * @version 5.0.0
 * @author Viney Bishnoi
 */
class GWPManager {
  constructor() {
    this.isProcessing = false;
    this.checkTimer = null;
    this.postSkioTimer = null;
    this.isUpdatingUI = false;
    this.cleanupInFlight = false;
    this.cleanupQueued = false;
    this.cleanupScheduleTimer = null;
    this.cartFetchInFlight = null;
    this.cachedCart = null;
    this.cachedCartAt = 0;
    this.lastCleanupSignature = '';
    this.lastCleanupAt = 0;
    this.lastCheckStartedAt = 0;
    this.minCheckIntervalMs = 200;
    this.checkCartMaxAgeMs = 350;
    this.lastNotificationMessage = '';
    this.lastNotificationAt = 0;
    this.debugSlowThresholdMs = 220;
    this.config = window.gwp?.config;
    this.miniCart = null;
    this.optOutMemory = null;
    this.optOutPersistFailed = false;
    this.customerActionInFlight = new Set();
    this.cartMutationSeq = 0;
    this.recheckPending = false;
    this.repaintPending = false;
    this.giftVerifyTimer = null;
    this.giftVerifyCounts = new Map();
    this.cartObserver = null;
    this.syncFrame = null;
    this.started = false;
    this.configRequest = null;
    this.configRetryDelays = [1000, 3000, 10000];
    this.configRetries = 0;
    this.configRetryTimer = null;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  getPropertyValue(properties, keys) {
    if (!properties) return undefined;
    for (const key of keys) {
      if (properties[key] !== undefined && properties[key] !== null && properties[key] !== '') {
        return properties[key];
      }
    }
    return undefined;
  }

  isTruthy(value) {
    if (value === true || value === 1) return true;
    if (typeof value !== 'string') return false;
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  isGiftItem(item) {
    return this.isTruthy(this.getPropertyValue(item?.properties, ['_isFree', '_is_free', 'isFree']));
  }

  isOnwardItem(item) {
    return item?.vendor === 'Onward' || item?.vendor === 'onward';
  }

  isFreeOrAuxItem(item) {
    return this.isGiftItem(item) || this.isOnwardItem(item);
  }

  // From the customer's default address; cart section re-renders carry a fresher marker than the page config.
  getRestrictedState() {
    const markers = document.querySelectorAll('[data-gwp-restricted-state]');
    const marker = markers[markers.length - 1];
    return String(marker ? marker.dataset.gwpRestrictedState : this.config?.restrictedState || '').trim();
  }

  getThresholdCents(item) {
    const value = this.getPropertyValue(item?.properties, ['_hasTh', '_has_th', 'hasTh']);
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  }

  init() {
    if (this.config) {
      this.start();
      return;
    }

    if (!window.gwp?.deferConfig) {
      console.error('🚫 GWP Manager: No configuration found. Ensure window.gwp.config is available.');
      return;
    }

    // Meta lander pages ship without the config; it is fetched once the cart is in play.
    const load = () => {
      // The event means the cart changed since the read below, so start() must not reuse it.
      this.cachedCartAt = 0;
      this.loadDeferredConfig();
    };
    ['cart:updated', 'cartUpdated', 'ajaxProduct:added', 'cartdrawer:opened'].forEach((event) => {
      document.addEventListener(event, load);
    });
    document.addEventListener('click', (event) => {
      if (this.started) return;
      const button = event.target.closest('.gwp-gift-remove, .gwp-gift-restore__button');
      if (!button) return;
      event.preventDefault();
      this.loadDeferredConfig().then((ready) => {
        if (!ready || !button.isConnected) return;
        if (button.matches('.gwp-gift-remove')) this.removeGiftByCustomer(button);
        else this.restoreGiftByCustomer(button);
      });
    });
    this.getCurrentCart({ force: true })
      .then((cart) => {
        if (cart?.item_count > 0) this.loadDeferredConfig();
      })
      .catch(() => {});
  }

  loadDeferredConfig() {
    if (this.started || !window.gwp?.deferConfig) return Promise.resolve(this.started);

    if (!this.configRequest) {
      this.configRequest = fetch(`${window.location.pathname}?section_id=gwp-config`)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gwp-config]');
          this.config = JSON.parse(node.textContent);
          window.gwp.config = this.config;
          this.start();
          return true;
        })
        .catch((error) => {
          this.configRequest = null;
          console.error('🚫 GWP Manager: Could not load the gift configuration.', error);
          this.logDebug('config.load.error', { message: error?.message || String(error) });
          // The cart change that asked for the config is still unhandled, so retry without waiting for another.
          const delay = this.configRetryDelays[this.configRetries];
          if (delay !== undefined) {
            this.configRetries += 1;
            clearTimeout(this.configRetryTimer);
            this.configRetryTimer = setTimeout(() => this.loadDeferredConfig(), delay);
          }
          return false;
        });
    }
    return this.configRequest;
  }

  start() {
    if (this.started) return;
    this.started = true;

    this.miniCart = document.querySelector('mini-cart');

    const cartUpdateEvents = ['cart:updated', 'cartUpdated', 'ajaxProduct:added'];
    cartUpdateEvents.forEach((event) => {
      document.addEventListener(event, this.handleCartUpdate.bind(this));
    });

    if (window.subscribe?.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
    }

    document.addEventListener('cartdrawer:opened', () => {
      this.miniCart = document.querySelector('mini-cart');
      this.syncOptOutUI();
      // The drawer loads fresh HTML, but cachedCart may predate a removal made in
      // another tab; without a re-read the row stays hidden next to no gift line.
      if (this.isRemovalAllowed() && Object.keys(this.getOptOutState()).length) {
        this.getCurrentCart({ force: true })
          .then(() => this.syncOptOutUI())
          .catch(() => {});
      }
    });

    document.addEventListener('shopify:section:load', () => {
      this.miniCart = document.querySelector('mini-cart');
      this.observeCartRenders();
      this.syncOptOutUI();
    });

    this.exposeCleanupApi();
    this.setupGiftRemoveHandlers();
    this.observeCartRenders();
    this.checkGWPConditions();
    this.scheduleCleanupRun(200);
    this.logDebug('init', {
      thresholdEnabled: !!this.config?.enableThreshold,
      bogoCampaigns: this.getBogoCampaigns().length,
    });
  }

  handleCartUpdate(event) {
    const source = event?.detail?.source || event?.source;
    const eventCart = event?.detail?.cart;

    // Warm cache from event payload to avoid a /cart.js fetch later.
    if (eventCart?.items) {
      this.updateCartCache(eventCart);
    }

    // Before the source filter: other scripts replace the cart markup and then
    // dispatch events this method ignores, which would leave the restore row in
    // whatever state their fresh HTML shipped with.
    this.syncOptOutUI();

    if (source === 'gwp-manager' || source === 'gwp-gift-cleanup') {
      return;
    }

    if (this.isProcessing || this.isUpdatingUI) {
      // Don't drop the event — a removal followed quickly by a quantity change
      // would otherwise settle on a state matching neither cart nor opt-out.
      this.recheckPending = true;
      return;
    }

    clearTimeout(this.checkTimer);

    this.checkTimer = setTimeout(() => {
      this.checkGWPConditions();
    }, 50);
  }

  /**
   * Every mutation captures this before its request and re-checks it after.
   * A response that predates a newer committed mutation must not be rendered:
   * doing so puts a removed gift back on screen and reverts cachedCart with it.
   */
  isStaleRender(seqBefore) {
    return this.cartMutationSeq !== seqBefore || this.customerActionInFlight.size > 0;
  }

  commitRender() {
    this.cartMutationSeq += 1;
  }

  /**
   * Both cart surfaces are replaced by innerHTML, by several scripts — some of
   * which (gift-wrapping.js) dispatch no cart event at all. Watching the
   * containers catches every repaint instead of chasing each caller.
   * childList only: toggling `hidden` is an attribute change, so this cannot
   * re-trigger itself.
   */
  observeCartRenders() {
    if (typeof MutationObserver === 'undefined') return;

    // Re-attachable: the theme editor replaces whole section elements, which
    // would otherwise leave the observer bound to a detached node.
    if (this.cartObserver) this.cartObserver.disconnect();

    this.cartObserver = new MutationObserver(() => {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncOptOutUI();
      });
    });

    ['mini-cart', 'main-cart-items'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) this.cartObserver.observe(el, { childList: true, subtree: true });
    });
  }

  /**
   * A superseded add leaves its gift in the cart but off screen, and the recheck
   * that follows finds nothing to change, so nothing else would repaint it.
   */
  async repaintCartSections() {
    this.repaintPending = false;
    const seqBefore = this.cartMutationSeq;
    try {
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(cartUpdateUrl, {
        ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
        body: JSON.stringify({
          updates: {},
          sections: this.getCleanupSectionsToRender(),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) return;

      if (this.isStaleRender(seqBefore)) {
        this.repaintPending = true;
        return;
      }
      this.commitRender();
      this.updateCartCache(result);
      if (result.sections) this.renderCleanupSections(result.sections);
      this.logDebug('ui.repaintCartSections', { itemCount: result?.item_count });
    } catch (error) {
      this.logDebug('ui.repaintCartSections.error', { message: error?.message || String(error) });
    }
  }

  drainRecheck() {
    if (this.isProcessing || this.customerActionInFlight.size) return;

    // Cleanup can be deferred while a customer action holds the lock, in which
    // case no in-flight cleanup exists to drain the queue on its way out.
    if (this.cleanupQueued && !this.cleanupInFlight) {
      this.cleanupQueued = false;
      this.scheduleCleanupRun(60);
    }

    if (!this.recheckPending) return;
    this.recheckPending = false;
    clearTimeout(this.checkTimer);
    this.checkTimer = setTimeout(() => this.checkGWPConditions(), 60);
  }

  schedulePostSkioUpdate() {
    if (!this.started) {
      this.loadDeferredConfig();
      return;
    }
    clearTimeout(this.postSkioTimer);

    this.postSkioTimer = setTimeout(() => {
      this.checkGWPConditions();
    }, 120);
  }

  exposeCleanupApi() {
    window.gwpGiftCleanup = {
      run: () => this.runGiftCleanupInternal(),
      scheduleRun: (delay) => this.scheduleCleanupRun(delay),
      getDebugLogs: () => this.getDebugLogs(),
      clearDebugLogs: () => this.clearDebugLogs(),
      get inFlight() {
        return !!window.gwpManager?.cleanupInFlight;
      },
    };
  }

  scheduleCleanupRun(delay = 120) {
    if (this.isProcessing || this.cleanupInFlight) {
      this.cleanupQueued = true;
      this.logDebug('cleanup.defer-busy', { source: 'schedule-call', delay });
      return;
    }
    clearTimeout(this.cleanupScheduleTimer);
    this.logDebug('cleanup.schedule', { delay });
    this.cleanupScheduleTimer = setTimeout(() => {
      this.runGiftCleanupInternal().catch(() => {});
    }, delay);
  }

  updateCartCache(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    this.cachedCart = cart;
    this.cachedCartAt = Date.now();
  }

  getCartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    const itemsSig = cart.items
      .map((item) => {
        const marker = item?.properties?._giftCampaign || '';
        const free = this.isGiftItem(item) ? 1 : 0;
        return `${item.key || item.id || item.variant_id}:${item.quantity || 0}:${item.final_line_price || 0}:${marker}:${free}`;
      })
      .join(',');

    return `${cart.item_count || cart.items.length}|${cart.total_price || 0}|${itemsSig}`;
  }

  isDebugEnabled() {
    // Production-safe default: debug is opt-in from browser console only.
    return window.gwpDebug === true;
  }

  getDebugLogs() {
    return Array.isArray(window.__gwpDebugLogs) ? [...window.__gwpDebugLogs] : [];
  }

  clearDebugLogs() {
    window.__gwpDebugLogs = [];
  }

  logDebug(event, data = {}) {
    if (!this.isDebugEnabled()) return;

    if (!Array.isArray(window.__gwpDebugLogs)) {
      window.__gwpDebugLogs = [];
    }

    const entry = {
      ts: new Date().toISOString(),
      event,
      ...data,
    };

    window.__gwpDebugLogs.push(entry);
    if (window.__gwpDebugLogs.length > 250) {
      window.__gwpDebugLogs.splice(0, window.__gwpDebugLogs.length - 250);
    }

    // Keep console output concise and structured for copy/paste diagnostics.
    console.log('[GWP DEBUG]', entry);
  }

  nowMs() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  /**
   * Active BOGO campaigns from config. Each entry:
   *   { campaign, giftProduct: [...], triggerProducts: [...], giftAsSubscription, giftOnlyWhenSubscription }
   * The campaign marker ('bogo', 'bogo2', …) is written to the _giftCampaign
   * line-item property so campaigns never collide.
   */
  getBogoCampaigns() {
    return Array.isArray(this.config?.bogoCampaigns) ? this.config.bogoCampaigns.filter(Boolean) : [];
  }

  /**
   * True when at least one campaign is enabled.
   */
  isAnyCampaignActive() {
    return !!(this.config && (this.config.enableThreshold || this.getBogoCampaigns().length));
  }

  getComboConfig() {
    const combo = this.config?.comboDiscount;
    if (!combo || !Array.isArray(combo.productIds) || !combo.productIds.length) return null;
    const percentage = Number(combo.percentage);
    if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) return null;
    return combo;
  }

  // Reads only _isFree and _freeSample, like the combo discount function and the Liquid snippet.
  isComboEligibleLine(item, combo) {
    if (!item || this.isOnwardItem(item) || item.gift_card) return false;
    if (this.isTruthy(this.getPropertyValue(item.properties, ['_isFree']))) return false;
    if (this.isTruthy(this.getPropertyValue(item.properties, ['_freeSample']))) return false;
    return !(combo?.excludeSubscriptions && item.selling_plan_allocation);
  }

  isComboQualified(cart) {
    const combo = this.getComboConfig();
    if (!combo) return false;

    const present = new Set();
    (cart?.items || []).forEach((item) => {
      if (this.isComboEligibleLine(item, combo)) present.add(Number(item.product_id));
    });
    const productIds = combo.productIds.map(Number);
    return combo.matchAny ? productIds.some((id) => present.has(id)) : productIds.every((id) => present.has(id));
  }

  // /cart.js reports a Function discount with an empty type, so only codes are ruled out; its title is the combo label.
  isComboApplied(cart) {
    const combo = this.getComboConfig();
    const percentage = Number(combo?.percentage);
    const label = this.stripLikeLiquid(combo?.label).toLowerCase();
    return (cart?.cart_level_discount_applications || []).some(
      (discount) =>
        discount.type !== 'discount_code' &&
        discount.value_type === 'percentage' &&
        Math.abs(Number(discount.value) - percentage) < 0.005 &&
        !!label &&
        this.stripLikeLiquid(discount.title).toLowerCase() === label,
    );
  }

  // Liquid's strip only removes ASCII whitespace; trimming the same way keeps JS and Liquid in agreement.
  stripLikeLiquid(value) {
    return String(value ?? '')
      .replace(/^[ \t\n\v\f\r]+/, '')
      .replace(/[ \t\n\v\f\r\0]+$/, '');
  }

  // Applied, not just qualified: an inactive combo or a better order code that doesn't combine leaves gifts alone.
  isComboExclusiveActive(cart) {
    return !!this.getComboConfig()?.exclusive && this.isComboQualified(cart) && this.isComboApplied(cart);
  }

  isGiftSuppressed(cart) {
    return !!this.getRestrictedState() || this.isComboExclusiveActive(cart);
  }

  /**
   * Runs both campaigns independently. Each can add/remove its own gift
   * without affecting the other.
   */
  async checkGWPConditions() {
    // Without config every campaign looks off, and cleanup would strip valid gifts.
    if (!this.config) return;
    if (this.isProcessing || this.customerActionInFlight.size) {
      // Defer rather than drop. A dropped check here leaves the restore button
      // dead and makes the min-interval re-arm lossy.
      this.recheckPending = true;
      return;
    }
    const nowWall = Date.now();
    if (nowWall - this.lastCheckStartedAt < this.minCheckIntervalMs) {
      this.logDebug('check.skip.min-interval', {
        sinceLastMs: nowWall - this.lastCheckStartedAt,
        minIntervalMs: this.minCheckIntervalMs,
      });
      // Re-arm for the remainder of the window rather than dropping the check.
      clearTimeout(this.checkTimer);
      this.checkTimer = setTimeout(
        () => this.checkGWPConditions(),
        this.minCheckIntervalMs - (nowWall - this.lastCheckStartedAt) + 10,
      );
      return;
    }

    this.lastCheckStartedAt = nowWall;
    this.isProcessing = true;
    const start = this.nowMs();
    let thresholdMutated = false;
    let bogoMutated = false;

    try {
      const cart = await this.getCurrentCart({ maxAgeMs: this.checkCartMaxAgeMs });

      // Cleanup removes gifts of suppressed, disabled or orphaned campaigns without opening the drawer.
      if (this.isGiftSuppressed(cart) || !this.isAnyCampaignActive()) {
        await this.runGiftCleanupInternal();
        return;
      }

      // --- Threshold campaign ---
      if (this.config.enableThreshold) {
        thresholdMutated = await this.handleThresholdCampaign(cart);
      }

      // Prefer recently updated cache after threshold mutation to avoid an extra
      // forced /cart.js roundtrip on the hot path.
      const freshCart = thresholdMutated
        ? await this.getCurrentCart({ force: false, maxAgeMs: 1800 })
        : cart;

      // --- BOGO campaigns (data-driven, independent, stackable) ---
      // Gifts belonging to a disabled/removed campaign are reconciled by the
      // cleanup pass below — evaluateInvalidGiftLines treats any bogo marker that
      // is not an active campaign as invalid and removes it.
      if (this.getBogoCampaigns().length) {
        bogoMutated = await this.handleBogoCampaigns(freshCart);
      }

      // Run cleanup inline — always use fresh cache since campaigns or
      // the threshold campaign may have mutated the cart above.
      await this.runGiftCleanupInternal();
    } catch (error) {
      console.error('GWP Error:', error);
      this.logDebug('check.error', { message: error?.message || String(error) });
    } finally {
      this.isProcessing = false;
      this.syncOptOutUI();
      // Before draining: a pending recheck takes the repaint with it on its way out.
      if (this.repaintPending && !this.recheckPending && !this.customerActionInFlight.size) {
        this.repaintCartSections();
      }
      this.drainRecheck();
      const duration = Math.round(this.nowMs() - start);
      this.logDebug('check.complete', {
        durationMs: duration,
        thresholdMutated,
        bogoMutated,
        slow: duration > this.debugSlowThresholdMs,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Cart helpers
  // ---------------------------------------------------------------------------

  getSectionsToRender() {
    if (this.miniCart?.getSectionsToRender) {
      return this.miniCart.getSectionsToRender().map((s) => s.id);
    }
    // No <mini-cart> on /cart, so fall back to the cleanup list — otherwise gift
    // mutations there request no sections and nothing repaints.
    return this.getCleanupSectionsToRender();
  }

  updateCartUI(result) {
    this.isUpdatingUI = true;
    this.updateCartCache(result);
    const start = this.nowMs();

    try {
      if (this.miniCart?.renderContents) {
        this.miniCart.renderContents(result);
      } else if (result?.sections) {
        this.renderCleanupSections(result.sections);
      }
    } finally {
      // Clear flag after a single frame so other listeners can render promptly.
      requestAnimationFrame(() => {
        this.isUpdatingUI = false;
        this.syncOptOutUI();
        // Clearing isUpdatingUI has to drain what it caused to be deferred.
        this.drainRecheck();
      });
      this.logDebug('ui.updateCartUI', {
        durationMs: Math.round(this.nowMs() - start),
        hasSections: !!result?.sections,
        itemCount: result?.item_count,
      });
    }
  }

  /**
   * Checks campaign gifts are visible in DOM using cached cart.
   * Only triggers a forced refresh if a gift is in the cart but missing from DOM.
   */
  ensureGiftsAreVisible() {
    if (!this.cachedCart) return;

    const campaignsToCheck = [];
    if (this.config.enableThreshold && this.config.thGiftProduct?.length) {
      campaignsToCheck.push({ gift: this.config.thGiftProduct[0], campaign: 'threshold' });
    }
    this.getBogoCampaigns().forEach((campaign) => {
      if (campaign.giftProduct?.length) {
        campaignsToCheck.push({ gift: campaign.giftProduct[0], campaign: campaign.campaign });
      }
    });

    if (!campaignsToCheck.length) return;

    let needsRefresh = false;
    const giftElements = document.querySelectorAll('[data-variant-id]');

    campaignsToCheck.forEach(({ gift, campaign }) => {
      const hasInCart = this.hasGiftProduct(this.cachedCart, [gift], campaign);
      if (!hasInCart) return;

      let visible = false;
      giftElements.forEach((el) => {
        if (el.dataset.variantId == gift.variantId) visible = true;
      });
      if (!visible) needsRefresh = true;
    });

    if (needsRefresh) this.forceCartRefresh();
  }

  forceCartRefresh() {
    const miniCart = document.querySelector('mini-cart');
    if (!miniCart) return;

    this.getCurrentCart({ force: true })
      .then((freshCart) => {
        if (miniCart.renderContents) {
          miniCart.renderContents({ ...freshCart, sections: {} });
        }
      })
      .catch(console.error);
  }

  async getCurrentCart({ force = false, maxAgeMs = 160 } = {}) {
    const now = Date.now();
    if (!force && this.cachedCart && now - this.cachedCartAt < maxAgeMs) {
      this.logDebug('cart.fetch.cache-hit', { force, maxAgeMs, ageMs: now - this.cachedCartAt });
      return this.cachedCart;
    }

    // Only reuse an inflight fetch if not forcing — a forced fetch needs data
    // from after the latest cart mutation, which an earlier inflight may predate.
    if (!force && this.cartFetchInFlight) {
      this.logDebug('cart.fetch.inflight-reuse', { force, maxAgeMs });
      return this.cartFetchInFlight;
    }

    const start = this.nowMs();
    this.logDebug('cart.fetch.network-start', { force, maxAgeMs });
    this.cartFetchInFlight = fetch(`/cart.js?t=${now}`)
      .then(async (response) => {
        if (!response.ok) throw new Error('Failed to fetch cart data');
        const cart = await response.json();
        this.updateCartCache(cart);
        this.logDebug('cart.fetch.network-success', {
          force,
          maxAgeMs,
          durationMs: Math.round(this.nowMs() - start),
          itemCount: cart?.item_count,
        });
        return cart;
      })
      .catch((error) => {
        this.logDebug('cart.fetch.network-error', {
          force,
          maxAgeMs,
          durationMs: Math.round(this.nowMs() - start),
          message: error?.message || String(error),
        });
        throw error;
      })
      .finally(() => {
        this.cartFetchInFlight = null;
      });

    return this.cartFetchInFlight;
  }

  evaluateInvalidGiftLines(cart) {
    const lines = cart?.items || [];
    const freeOrAuxLines = lines.filter((line) => this.isFreeOrAuxItem(line));

    // If every item is a gift or Onward (no paid products), remove them all
    if (freeOrAuxLines.length === lines.length && lines.length > 0) {
      return freeOrAuxLines;
    }

    const giftLines = lines.filter((line) => this.isGiftItem(line));
    if (!giftLines.length) return [];
    if (this.isGiftSuppressed(cart)) return giftLines;

    const nonGiftSubtotal = lines.reduce((sum, line) => {
      if (this.isFreeOrAuxItem(line)) return sum;
      return sum + (line.final_line_price || 0);
    }, 0);

    // Build per-campaign BOGO validation state, keyed by campaign marker:
    //   marker -> { triggerProductIds: Set, giftProductIds: Set }
    // Each campaign honours its own subscription gate independently.
    const bogoState = new Map();
    this.getBogoCampaigns().forEach((campaign) => {
      const requireSubscription = !!campaign.giftOnlyWhenSubscription;
      const triggerProductIds = new Set();
      if ((campaign.triggerProducts || []).length) {
        lines.forEach((line) => {
          if (this.isFreeOrAuxItem(line)) return;
          const isTrigger = campaign.triggerProducts.some((t) => t.id === line.product_id);
          if (isTrigger && (!requireSubscription || line.selling_plan_allocation)) {
            triggerProductIds.add(line.product_id);
          }
        });
      }
      bogoState.set(campaign.campaign, {
        triggerProductIds,
        giftProductIds: new Set((campaign.giftProduct || []).map((g) => g.id)),
      });
    });

    // Track which campaigns already have one valid gift — duplicates are invalid.
    const seenCampaign = new Set();

    return giftLines.filter((line) => {
      const campaign = line.properties?._giftCampaign;

      // Threshold gift: invalid if below threshold OR duplicate
      if (campaign === 'threshold') {
        if (!this.config?.enableThreshold) return true;
        if (seenCampaign.has('threshold')) return true;

        const configuredThreshold = Math.round(Number(this.config?.threshold || 0) * 100);
        const thresholdCents = this.getThresholdCents(line) ?? configuredThreshold;
        if (!Number.isFinite(thresholdCents) || thresholdCents <= 0) return true;

        if (nonGiftSubtotal < thresholdCents) return true;

        seenCampaign.add('threshold');
        return false;
      }

      // BOGO gift: invalid if its campaign is disabled/removed, the product is not
      // that campaign's configured gift, no trigger is present, OR it is a duplicate.
      if (bogoState.has(campaign)) {
        if (seenCampaign.has(campaign)) return true;
        const state = bogoState.get(campaign);
        if (!state.giftProductIds.has(line.product_id)) return true;
        if (state.triggerProductIds.size === 0) return true;

        seenCampaign.add(campaign);
        return false;
      }

      // Any free line without a recognized/active campaign marker is orphaned
      // (this also covers gifts from a BOGO campaign that has since been disabled).
      return true;
    });
  }

  getCleanupSectionsToRender() {
    const sections = [];
    const mappings = [
      document.getElementById('mini-cart')?.id,
      document.getElementById('main-cart-items')?.dataset.id,
      document.getElementById('main-cart-footer')?.dataset.id,
      'cart-icon-bubble',
      'mobile-cart-icon-bubble',
      'cart-live-region-text',
    ];
    mappings.forEach((id) => {
      if (id) sections.push(id);
    });
    return [...new Set(sections)];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  renderCleanupSections(sections) {
    if (!sections) return;
    const start = this.nowMs();
    let updatedTargets = 0;
    const targets = [
      { id: 'mini-cart', section: document.getElementById('mini-cart')?.id, selector: '.shopify-section' },
      { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
    ];

    targets.forEach((target) => {
      if (!target.section || !sections[target.section]) return;
      const element = document.getElementById(target.id);
      if (!element) return;
      const elementToReplace = element.querySelector(target.selector) || element;
      const nextHTML = this.getSectionInnerHTML(sections[target.section], target.selector);
      if (nextHTML) {
        elementToReplace.innerHTML = nextHTML;
        updatedTargets += 1;
      }
    });
    this.logDebug('ui.renderCleanupSections', {
      durationMs: Math.round(this.nowMs() - start),
      updatedTargets,
    });
    this.syncOptOutUI();
  }

  getGiftQtyCorrections(cart) {
    const corrections = {};
    (cart?.items || []).forEach((item) => {
      if (this.isGiftItem(item) && item.quantity > 1) {
        corrections[item.key] = 1;
      }
    });
    return corrections;
  }

  async removeGiftLinesBatch(lineKeys, { qtyCorrections = {}, retry = true } = {}) {
    const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
    const start = this.nowMs();
    const updates = { ...qtyCorrections };
    lineKeys.forEach((key) => {
      updates[key] = 0;
    });

    const response = await fetch(cartUpdateUrl, {
      ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
      body: JSON.stringify({
        updates,
        sections: this.getCleanupSectionsToRender(),
        sections_url: window.location.pathname,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      this.logDebug('cleanup.removeGiftLinesBatch.error', {
        durationMs: Math.round(this.nowMs() - start),
        retry,
        lineCount: lineKeys.length,
        status: response.status,
      });
      // Retry once with fresh cart keys in case the keys went stale
      if (retry) {
        const freshCart = await this.getCurrentCart({ force: true });
        const freshInvalid = this.evaluateInvalidGiftLines(freshCart);
        const freshCorrections = this.getGiftQtyCorrections(freshCart);
        const freshInvalidKeys = new Set(freshInvalid.map((l) => l.key));
        Object.keys(freshCorrections).forEach((k) => {
          if (freshInvalidKeys.has(k)) delete freshCorrections[k];
        });
        if (freshInvalid.length || Object.keys(freshCorrections).length) {
          return this.removeGiftLinesBatch(
            freshInvalid.map((line) => line.key),
            { qtyCorrections: freshCorrections, retry: false },
          );
        }
      }
      throw new Error(result.description || 'Failed to remove gift line(s)');
    }
    this.logDebug('cleanup.removeGiftLinesBatch.success', {
      durationMs: Math.round(this.nowMs() - start),
      lineCount: lineKeys.length,
    });
    return result;
  }

  async runGiftCleanupInternal(existingCart) {
    if (!this.config) return { removed: 0 };
    if (this.cleanupInFlight || this.customerActionInFlight.size) {
      this.cleanupQueued = true;
      return { removed: 0 };
    }

    this.cleanupInFlight = true;
    let removed = 0;
    const start = this.nowMs();
    let skipReason = '';

    try {
      // Use provided cart or fetch fresh — avoids extra /cart.js roundtrip.
      let cart = existingCart || await this.getCurrentCart({ maxAgeMs: 520 });
      const initialSignature = this.getCartSignature(cart);
      if (initialSignature && initialSignature === this.lastCleanupSignature && Date.now() - this.lastCleanupAt < 420) {
        skipReason = 'same-signature-window';
        return { removed: 0 };
      }

      const invalidGiftLines = this.evaluateInvalidGiftLines(cart);
      const qtyCorrections = this.getGiftQtyCorrections(cart);
      // Exclude keys already marked for removal from corrections
      const invalidKeys = new Set(invalidGiftLines.map((line) => line.key));
      Object.keys(qtyCorrections).forEach((key) => {
        if (invalidKeys.has(key)) delete qtyCorrections[key];
      });

      const hasWork = invalidGiftLines.length > 0 || Object.keys(qtyCorrections).length > 0;

      if (hasWork) {
        const lineKeys = invalidGiftLines.map((line) => line.key);
        removed = lineKeys.length;
        const seqBefore = this.cartMutationSeq;
        const updateResult = await this.removeGiftLinesBatch(lineKeys, { qtyCorrections });

        if (this.isStaleRender(seqBefore)) {
          skipReason = 'superseded';
          this.cleanupQueued = true;
          this.cachedCartAt = 0;
          this.repaintPending = true;
          removed = 0;
          return { removed: 0 };
        }
        this.commitRender();

        this.updateCartCache(updateResult);
        if (updateResult?.sections) {
          this.renderCleanupSections(updateResult.sections);
        }
        cart = updateResult?.items ? updateResult : await this.getCurrentCart({ force: true });
        this.updateCartCache(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'gwp-gift-cleanup', cart } }));
      }

      this.lastCleanupSignature = this.getCartSignature(cart);
      this.lastCleanupAt = Date.now();

      // Restricted and exclusive-combo shoppers get a persistent notice row instead of a toast.
      if (removed > 0 && !this.isGiftSuppressed(cart)) {
        this.showNotification('Free gift updated based on cart eligibility.', 'info');
      }
    } catch (error) {
      console.error('[GWP Cleanup] Error:', error);
      this.logDebug('cleanup.error', { message: error?.message || String(error) });
    } finally {
      const duration = Math.round(this.nowMs() - start);
      this.logDebug('cleanup.complete', {
        durationMs: duration,
        removed,
        skipReason,
        queuedNext: !!this.cleanupQueued,
        slow: duration > this.debugSlowThresholdMs,
      });
      this.cleanupInFlight = false;
      if (this.cleanupQueued) {
        this.cleanupQueued = false;
        this.runGiftCleanupInternal().catch(() => {});
      }
    }

    return { removed };
  }

  // ---------------------------------------------------------------------------
  // Threshold campaign
  // ---------------------------------------------------------------------------

  isThresholdEligible(cart) {
    if (!this.config?.threshold || !this.config.thGiftProduct?.length) return false;

    let cartTotal = 0;
    (cart?.items || []).forEach((item) => {
      if (!this.isFreeOrAuxItem(item)) {
        cartTotal += item.final_line_price;
      }
    });

    return cartTotal >= this.config.threshold * 100;
  }

  async handleThresholdCampaign(cart) {
    if (!this.config.threshold || !this.config.thGiftProduct?.length) return false;

    const meetsThreshold = this.isThresholdEligible(cart);
    const hasGift = this.hasGiftProduct(cart, this.config.thGiftProduct, 'threshold');

    if (meetsThreshold && !hasGift) {
      const added = await this.addGiftProduct(
        this.config.thGiftProduct[0],
        'threshold',
        this.config.giftAsSubscription,
      );
      return added !== false;
    } else if (!meetsThreshold && hasGift) {
      await this.removeGiftProducts(cart, this.config.thGiftProduct, 'threshold');
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  // BOGO campaigns (data-driven; any number run independently and stack)
  // ---------------------------------------------------------------------------

  /**
   * Runs every configured BOGO campaign. Each campaign adds/removes its own gift
   * (keyed by its `campaign` marker) without affecting the others, so a shopper
   * who qualifies for multiple campaigns receives every gift.
   * @param {object} cart
   * @returns {Promise<boolean>} true if any campaign mutated the cart.
   */
  isBogoCampaignEligible(campaign, cart) {
    if (!campaign?.triggerProducts?.length || !campaign.giftProduct?.length) return false;

    const requireSubscription = !!campaign.giftOnlyWhenSubscription;

    return (cart?.items || []).some(
      (item) =>
        !this.isFreeOrAuxItem(item) &&
        (!requireSubscription || item.selling_plan_allocation) &&
        campaign.triggerProducts.some((trigger) => trigger.id === item.product_id),
    );
  }

  async handleBogoCampaigns(cart) {
    const campaigns = this.getBogoCampaigns();
    if (!campaigns.length) return false;

    let mutated = false;

    for (const campaign of campaigns) {
      if (!campaign.triggerProducts?.length || !campaign.giftProduct?.length) continue;

      const hasTrigger = this.isBogoCampaignEligible(campaign, cart);
      const hasGift = this.hasGiftProduct(cart, campaign.giftProduct, campaign.campaign);

      if (hasTrigger && !hasGift) {
        const added = await this.addGiftProduct(
          campaign.giftProduct[0],
          campaign.campaign,
          campaign.giftAsSubscription,
        );
        if (added !== false) mutated = true;
      } else if (!hasTrigger && hasGift) {
        await this.removeGiftProducts(cart, campaign.giftProduct, campaign.campaign);
        mutated = true;
      }
    }

    return mutated;
  }

  // ---------------------------------------------------------------------------
  // Gift detection / add / remove
  // ---------------------------------------------------------------------------

  hasGiftProduct(cart, giftProducts, campaignType) {
    return cart.items.some(
      (item) =>
        this.isGiftItem(item) &&
        item.properties?._giftCampaign === campaignType &&
        giftProducts.some((gift) => gift.id === item.product_id),
    );
  }

  /**
   * @param {object}  giftProduct       - The gift product to add.
   * @param {string}  campaignType      - 'threshold' | 'bogo' | 'bogo2' | …
   * @param {boolean} addAsSubscription - Whether to attach a selling plan.
   */
  async addGiftProduct(giftProduct, campaignType, addAsSubscription) {
    if (!giftProduct?.variantId) {
      console.error('Invalid gift product configuration:', giftProduct);
      return;
    }

    if (this.isGiftSuppressed(this.cachedCart)) {
      this.logDebug('add.skip.suppressed', { campaignType, productId: giftProduct.id });
      return false;
    }

    if ((addAsSubscription || this.isRemovalAllowed()) && this.hasOptedOut(campaignType, giftProduct)) {
      this.logDebug('add.skip.opted-out', { campaignType, productId: giftProduct.id });
      // false, so the caller does not report a mutation: while an opt-out is
      // held this path runs on every cart event and would force a cart re-read.
      return false;
    }

    try {
      const start = this.nowMs();
      const properties = {
        _isFree: 'true',
        _giftCampaign: campaignType,
      };

      if (campaignType === 'threshold') {
        properties._hasTh = this.config?.threshold;
      }

      const seqBefore = this.cartMutationSeq;
      const sections = this.getSectionsToRender();
      const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';

      const requestBody = {
        id: giftProduct.variantId,
        quantity: 1,
        properties,
        sections,
        sections_url: window.location.pathname,
      };

      if (addAsSubscription && giftProduct.hasSellingPlans && giftProduct.defaultSellingPlanId) {
        requestBody.selling_plan = giftProduct.defaultSellingPlanId;
        properties._addedAsSubscription = 'true';
      }

      const response = await fetch(cartAddUrl, {
        ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
        body: JSON.stringify(requestBody),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Failed to add gift');
      // add.js returns a line item, not a cart, so the cache no longer matches the
      // server; a recheck served from it would add this gift a second time.
      this.cachedCartAt = 0;

      if (this.isStaleRender(seqBefore)) {
        // A customer removal committed while this add was in flight. The gift is
        // in the cart server-side; rendering this response would show the cart
        // as it was before their removal. Let the next check repaint instead.
        this.recheckPending = true;
        this.repaintPending = true;
        this.logDebug('mutation.addGift.render-superseded', { campaignType });
        return;
      }
      this.commitRender();

      this.updateCartUI(result);
      this.scheduleGiftVerify(campaignType);
      this.logDebug('mutation.addGift.success', {
        campaignType,
        durationMs: Math.round(this.nowMs() - start),
        variantId: giftProduct.variantId,
      });

      const subscriptionText =
        addAsSubscription && giftProduct.hasSellingPlans && giftProduct.defaultSellingPlanId ? ' (Subscription)' : '';
      // The config carries ids only, so the name comes from the added line.
      const giftTitle = result.product_title ?? result.items?.[0]?.product_title;
      this.showNotification(
        giftTitle ? `🎁 Free gift added: ${giftTitle}${subscriptionText}` : `🎁 Free gift added${subscriptionText}`,
        'success',
      );
    } catch (error) {
      console.error('Error adding gift product:', error);
      this.logDebug('mutation.addGift.error', {
        campaignType,
        variantId: giftProduct?.variantId,
        message: error?.message || String(error),
      });
      this.showNotification(error.message, 'error');
    }
  }

  /**
   * Removes gifts for a specific campaign only.
   */
  async removeGiftProducts(cart, giftProducts, campaignType) {
    const giftItems = cart.items.filter(
      (item) =>
        this.isGiftItem(item) &&
        item.properties?._giftCampaign === campaignType &&
        giftProducts.some((gift) => gift.id === item.product_id),
    );
    if (!giftItems.length) return;
    await this.updateCartWithRemovals(giftItems, `Gift removed: ${giftItems[0].product_title}`);
  }

  async updateCartWithRemovals(itemsToRemove, notificationMessage, { retry = true } = {}) {
    try {
      const start = this.nowMs();
      const seqBefore = this.cartMutationSeq;
      const sections = this.getSectionsToRender();
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';

      const updates = {};
      itemsToRemove.forEach((item) => {
        updates[item.key] = 0;
      });

      const response = await fetch(cartUpdateUrl, {
        ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
        body: JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        this.logDebug('mutation.removeGift.error', {
          durationMs: Math.round(this.nowMs() - start),
          retry,
          removeCount: itemsToRemove.length,
          status: response.status,
        });
        // Retry once with fresh cart keys, scoped to the same variant IDs
        if (retry) {
          const targetVariantIds = new Set(itemsToRemove.map((item) => item.variant_id));
          const freshCart = await this.getCurrentCart({ force: true });
          const freshGifts = freshCart.items.filter(
            (item) => this.isFreeOrAuxItem(item) && targetVariantIds.has(item.variant_id),
          );
          if (freshGifts.length) {
            return this.updateCartWithRemovals(freshGifts, notificationMessage, { retry: false });
          }
          // Already gone (a customer removal got there first): not an error, but the
          // screen may still show the line, so repaint when the check finishes.
          this.repaintPending = true;
          return;
        }
        throw new Error(result.description || 'Failed to remove gift(s)');
      }

      if (this.isStaleRender(seqBefore)) {
        this.recheckPending = true;
        this.cachedCartAt = 0;
        this.repaintPending = true;
        this.logDebug('mutation.removeGift.render-superseded', { removeCount: itemsToRemove.length });
        return;
      }
      this.commitRender();

      this.updateCartUI(result);
      this.logDebug('mutation.removeGift.success', {
        durationMs: Math.round(this.nowMs() - start),
        removeCount: itemsToRemove.length,
      });
      this.showNotification(notificationMessage, 'info');
    } catch (error) {
      console.error('Error removing gift products:', error);
      this.logDebug('mutation.removeGift.exception', {
        removeCount: itemsToRemove?.length || 0,
        message: error?.message || String(error),
      });
      this.showNotification(error.message, 'error');
    }
  }

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  showNotification(message, type = 'info') {
    const now = Date.now();
    if (message === this.lastNotificationMessage && now - this.lastNotificationAt < 600) {
      return;
    }
    this.lastNotificationMessage = message;
    this.lastNotificationAt = now;

    // Remove any existing toast so the new one appears immediately.
    document.querySelectorAll('.gwp-toast').forEach((el) => el.remove());

    const icons = {
      success: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
      error: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
      info: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
    };

    const toast = document.createElement('div');
    toast.className = 'gwp-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    const iconSpan = document.createElement('span');
    iconSpan.className = 'gwp-toast__icon';
    iconSpan.innerHTML = icons[type] || icons.info;
    const textSpan = document.createElement('span');
    textSpan.className = 'gwp-toast__text';
    textSpan.textContent = message;
    toast.appendChild(iconSpan);
    toast.appendChild(textSpan);

    // Attach inside mini-cart so it stays within the drawer context.
    const container = document.getElementById('main-cart-items') || document.querySelector('.mini-cart__main') || document.body;
    container.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('gwp-toast--visible');
    });

    setTimeout(() => {
      toast.classList.remove('gwp-toast--visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      setTimeout(() => toast.remove(), 350);
    }, 3000);
  }

  // ---------------------------------------------------------------------------
  // Session-based opt-out for subscription gifts
  // ---------------------------------------------------------------------------

  isRemovalAllowed() {
    return !!this.config?.allowGiftRemoval;
  }

  getRemovalMode() {
    return this.isRemovalAllowed() && this.config?.removalPersistence === 'cookie' ? 'cookie' : 'session';
  }

  getCookieMaxAgeSeconds() {
    const days = Number(this.config?.removalCookieDays);
    const safeDays = Number.isFinite(days) && days > 0 ? Math.min(days, 400) : 30;
    return Math.round(safeDays * 86400);
  }

  readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  writeCookie(name, value) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // Session mode is a session cookie, not sessionStorage: the cart is shared by
    // every tab, so a per-tab opt-out lets any new tab re-add the removed gift.
    const maxAge = this.getRemovalMode() === 'cookie' ? `; max-age=${this.getCookieMaxAgeSeconds()}` : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/${maxAge}; SameSite=Lax${secure}`;
  }

  /**
   * Campaign ids that may hold an opt-out: 'threshold' plus every configured
   * BOGO campaign id. Unlike a fixed two-slot shape this handles bogo2/bogoN.
   */
  knownCampaignIds() {
    const ids = ['threshold'];
    this.getBogoCampaigns().forEach((campaign) => {
      if (campaign?.campaign) ids.push(campaign.campaign);
    });
    return ids;
  }

  /**
   * Shape: `{ <campaignId>: <giftProductId> }`. Storing the product id (not the
   * variant id) means the opt-out survives a first_available_variant change but
   * self-invalidates when the merchant swaps that campaign's gift — refusing one
   * gift is not refusing another.
   */
  getOptOutState() {
    // Only trust memory when the store itself is unusable. Reading through on
    // every call is what makes cookie mode correct across tabs: a sibling tab's
    // removal must be visible here, or this tab re-adds the gift.
    if (this.optOutPersistFailed && this.optOutMemory) return this.optOutMemory;

    let raw = null;
    try {
      raw = this.readCookie('gwpGiftOptOut');
    } catch (e) {
      return this.optOutMemory || {};
    }

    let parsed = null;
    try {
      parsed = raw ? JSON.parse(raw) : null;
    } catch (e) {
      parsed = null;
    }

    // Filter by id shape, not by the active campaigns: a campaign that is off for
    // a moment (low gift stock) must not lose its opt-out on an unrelated write.
    const state = {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      Object.keys(parsed).forEach((id) => {
        if (!/^(threshold|bogo\d{0,2})$/.test(id)) return;
        const productId = Number(parsed[id]);
        if (Number.isFinite(productId) && productId > 0) state[id] = productId;
      });
    }
    return state;
  }

  setOptOut(campaignType, productId, value) {
    if (!this.knownCampaignIds().includes(campaignType)) return;

    const state = this.getOptOutState();
    const id = Number(productId);
    if (value && Number.isFinite(id) && id > 0) {
      state[campaignType] = id;
    } else {
      delete state[campaignType];
    }
    this.optOutMemory = state;

    const serialized = JSON.stringify(state);
    // Storage can be unavailable (private mode, blocked site data). optOutMemory
    // already holds the decision, so the gift is not re-added on this page even
    // when the write below fails.
    try {
      this.writeCookie('gwpGiftOptOut', serialized);
      // Blocked cookies fail silently on assignment, so confirm by reading back.
      if (this.readCookie('gwpGiftOptOut') !== serialized) {
        throw new Error('cookie write rejected');
      }
    } catch (e) {
      this.optOutPersistFailed = true;
      this.logDebug('optOut.persist.failed', { campaignType, mode: this.getRemovalMode() });
    }
  }

  hasOptedOut(campaignType, giftProduct) {
    const stored = this.getOptOutState()[campaignType];
    if (!stored) return false;

    const productId = Number(giftProduct?.id ?? giftProduct);
    if (!Number.isFinite(productId)) return false;
    return stored === productId;
  }

  getCampaignGift(campaignType) {
    if (campaignType === 'threshold') return this.config?.thGiftProduct?.[0] || null;
    const campaign = this.getBogoCampaigns().find((c) => c?.campaign === campaignType);
    return campaign?.giftProduct?.[0] || null;
  }

  isCampaignEligible(campaignType, cart) {
    if (this.isGiftSuppressed(cart)) return false;
    return this.isCampaignQualified(campaignType, cart);
  }

  isCampaignQualified(campaignType, cart) {
    if (campaignType === 'threshold') {
      return !!this.config?.enableThreshold && this.isThresholdEligible(cart);
    }
    const campaign = this.getBogoCampaigns().find((c) => c?.campaign === campaignType);
    return !!campaign && this.isBogoCampaignEligible(campaign, cart);
  }

  setupGiftRemoveHandlers() {
    document.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('.gwp-gift-remove');
      if (removeBtn) {
        event.preventDefault();
        this.removeGiftByCustomer(removeBtn);
        return;
      }

      const restoreBtn = event.target.closest('.gwp-gift-restore__button');
      if (restoreBtn) {
        event.preventDefault();
        this.restoreGiftByCustomer(restoreBtn);
      }
    });
  }

  /**
   * Customer-initiated removal. Deliberately does NOT go through
   * <cart-remove-button>: that removes by positional line index while cleanup
   * removes by line key, and the two racing can zero a PAID line. This owns the
   * whole mutation — key-based, one request, cleanup-style repaint so the cart
   * page updates and the drawer is not force-opened.
   */
  async removeGiftByCustomer(button) {
    const campaignType = button.dataset.gwpCampaign;
    const lineKey = button.dataset.gwpLineKey;
    const productId = parseInt(button.dataset.gwpProductId, 10);
    if (!lineKey) return;
    // A line left over from a since-removed campaign still gets a working
    // button: the removal proceeds and setOptOut no-ops for the unknown id.

    // One customer action at a time: two removals in flight render independently,
    // and the older response can land last. Guard before disabling so a dropped
    // click leaves the control usable.
    if (this.customerActionInFlight.size) return;
    const keyboardFocus = this.hasKeyboardFocus(button);
    this.customerActionInFlight.add(campaignType);
    button.disabled = true;
    let removed = false;

    // Record the opt-out first: if the removal request then fails the gift is
    // still present and no add is attempted. The reverse order fails to a re-add.
    const priorOptOut = this.getOptOutState()[campaignType];
    this.setOptOut(campaignType, productId, true);

    try {
      const start = this.nowMs();
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(cartUpdateUrl, {
        ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
        body: JSON.stringify({
          updates: { [lineKey]: 0 },
          sections: this.getCleanupSectionsToRender(),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || 'Failed to remove gift');

      this.updateCartCache(result);
      if (result.sections) this.renderCleanupSections(result.sections);

      // Claim the cleanup dedupe window so the corrective pass does not
      // immediately re-render on top of this.
      if (!this.evaluateInvalidGiftLines(result).length && !Object.keys(this.getGiftQtyCorrections(result)).length) {
        this.lastCleanupSignature = this.getCartSignature(result);
        this.lastCleanupAt = Date.now();
      }
      // Marks this render as newer than any response still in flight elsewhere.
      this.commitRender();

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'gwp-manager', cart: result } }));
      // Always reconcile afterwards: this response could itself have been
      // overtaken by a mutation that committed while it was in flight.
      this.recheckPending = true;
      removed = true;
      this.showNotification('Free gift removed.', 'info');
      this.logDebug('mutation.customerRemoveGift.success', {
        campaignType,
        productId,
        durationMs: Math.round(this.nowMs() - start),
      });
    } catch (error) {
      // Restore the previous value, not null — another tab may already hold an
      // opt-out for this campaign and a failure here must not wipe it.
      this.setOptOut(campaignType, priorOptOut, priorOptOut !== null && priorOptOut !== undefined);
      console.error('Error removing gift product:', error);
      this.logDebug('mutation.customerRemoveGift.error', {
        campaignType,
        productId,
        message: error?.message || String(error),
      });
      this.showNotification(error.message, 'error');
    } finally {
      this.customerActionInFlight.delete(campaignType);
      // No-op on success (the line is gone with the re-render); on failure this
      // is what stops the control being stuck disabled.
      button.disabled = false;
      this.syncOptOutUI();
      if (keyboardFocus) {
        if (!removed || !this.focusCartControl(`.gwp-gift-restore__button[data-gwp-campaign="${campaignType}"]`)) {
          if (button.isConnected) button.focus();
        }
      }
      this.drainRecheck();
    }
  }

  async restoreGiftByCustomer(button) {
    const campaignType = button.dataset.gwpCampaign;
    if (!this.knownCampaignIds().includes(campaignType)) return;
    if (this.isGiftSuppressed(this.cachedCart)) return;

    if (this.customerActionInFlight.size) return;
    const keyboardFocus = this.hasKeyboardFocus(button);
    this.customerActionInFlight.add(campaignType);
    button.disabled = true;
    let released = false;

    try {
      this.setOptOut(campaignType, null, false);
      this.syncOptOutUI();
      // Bypass the min-interval guard: this is a direct customer action, not a
      // cart event, so it must not be coalesced away. Release the lock first or
      // checkGWPConditions defers on its own busy guard.
      this.lastCheckStartedAt = 0;
      this.customerActionInFlight.delete(campaignType);
      released = true;
      await this.checkGWPConditions();
      this.logDebug('mutation.customerRestoreGift', { campaignType });

      // Another tab may already have put the gift back: the check then changes
      // nothing and this tab would show neither the gift line nor the row.
      const gift = this.getCampaignGift(campaignType);
      if (
        gift &&
        this.hasGiftProduct(this.cachedCart || { items: [] }, [gift], campaignType) &&
        !document.querySelector(`.gwp-gift-remove[data-gwp-campaign="${campaignType}"], [data-variant-id="${gift.variantId}"]`)
      ) {
        await this.repaintCartSections();
      }
    } finally {
      // After release the key may belong to a removal that started meanwhile.
      if (!released) this.customerActionInFlight.delete(campaignType);
      button.disabled = false;
      this.syncOptOutUI();
      if (keyboardFocus && !this.focusCartControl(`.gwp-gift-remove[data-gwp-campaign="${campaignType}"]`)) {
        this.focusCartControl(`.gwp-gift-restore__button[data-gwp-campaign="${campaignType}"]`);
      }
      // checkGWPConditions defers when busy; make sure that deferral is drained
      // so a restore can never end with the opt-out cleared and no gift added.
      this.drainRecheck();
    }
  }

  hasKeyboardFocus(el) {
    if (el !== document.activeElement) return false;
    try {
      return el.matches(':focus-visible');
    } catch (e) {
      return true;
    }
  }

  focusCartControl(selector) {
    const el = document.querySelector(selector);
    if (!el || el.disabled || el.closest('[hidden]')) return false;
    el.focus();
    return true;
  }

  /**
   * Reveals the "Add it back" row only while an opt-out is held AND the campaign
   * would otherwise be adding a gift. Re-run after every render because both
   * cart templates are replaced wholesale via innerHTML.
   */
  syncOptOutUI() {
    this.syncRegionNotice();
    const rows = document.querySelectorAll('[data-gwp-restore-campaign]');
    if (!rows.length) return;

    const cart = this.cachedCart;
    rows.forEach((row) => {
      const campaignType = row.dataset.gwpRestoreCampaign;
      const gift = this.getCampaignGift(campaignType);
      const show =
        this.isRemovalAllowed() &&
        !!cart &&
        !!gift &&
        // Mid-removal the opt-out is already written while cachedCart still has
        // the gift, so without this the row shows alongside the line it replaces.
        !this.customerActionInFlight.has(campaignType) &&
        !this.hasGiftProduct(cart, [gift], campaignType) &&
        this.hasOptedOut(campaignType, gift) &&
        this.isCampaignEligible(campaignType, cart);
      row.hidden = !show;

      if (show) {
        // Clear any disabled state left behind by a previous action on this row.
        const btn = row.querySelector('.gwp-gift-restore__button');
        if (btn) btn.disabled = false;
      }
    });
  }

  syncRegionNotice() {
    const notices = document.querySelectorAll('[data-gwp-region-notice]');
    if (!notices.length) return;

    const cart = this.cachedCart;
    const campaigns = ['threshold', ...this.getBogoCampaigns().map((c) => c?.campaign).filter(Boolean)];
    const show =
      !!cart && !!this.getRestrictedState() && campaigns.some((campaignType) => this.isCampaignQualified(campaignType, cart));

    notices.forEach((notice) => {
      notice.hidden = !show;
    });
  }

  // Intelligems' first-add cartMetafieldsSet can overwrite the cart without the gift; re-check once it settles.
  scheduleGiftVerify(campaignType) {
    const count = this.giftVerifyCounts.get(campaignType) || 0;
    if (count >= 2) return;
    this.giftVerifyCounts.set(campaignType, count + 1);

    clearTimeout(this.giftVerifyTimer);
    this.giftVerifyTimer = setTimeout(() => {
      this.giftVerifyTimer = null;
      this.cachedCartAt = 0;
      this.lastCheckStartedAt = 0;
      this.logDebug('check.giftVerify', { campaignType });
      this.checkGWPConditions();
    }, 2500);
  }

  destroy() {
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    if (this.giftVerifyTimer) {
      clearTimeout(this.giftVerifyTimer);
      this.giftVerifyTimer = null;
    }
    if (this.postSkioTimer) {
      clearTimeout(this.postSkioTimer);
      this.postSkioTimer = null;
    }
  }
}

window.gwpManager = new GWPManager();
